import React, { useEffect, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { fetchMyStats } from "../lib/api";

/**
 * CombatStatsCard — per-league K/D/A + win rate + head-to-head record.
 *
 * If `userId` prop is missing, defaults to `/me/stats`. A `leagueId` filter
 * scopes to one league and unlocks the head-to-head table; without it the
 * card shows the totals row + per-league breakdown.
 *
 * Props:
 *   data?: pre-fetched payload (skips network)
 *   leagueId?: string
 */
export function CombatStatsCard({ data, leagueId, onPickLeague, onToast }) {
  const [d, setD] = useState(data || null);
  const [activeLeagueId, setActiveLeagueId] = useState(leagueId || null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (data && !leagueId) { setD(data); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    fetchMyStats(activeLeagueId).then((res) => {
      if (alive) { setD(res); setLoading(false); }
    }).catch((e) => {
      if (!alive) return;
      onToast?.(e?.response?.data?.detail || "Couldn't load stats.");
      setLoading(false);
    });
    return () => { alive = false; };
  }, [activeLeagueId, data, leagueId, onToast]);

  if (loading) {
    return (
      <div className="glass rounded-3xl p-5 border border-white/10 text-center text-white/55 text-sm">
        Loading combat stats…
      </div>
    );
  }
  if (!d) return null;

  const t = d.totals || {};
  const winRate = Math.round((t.win_rate || 0) * 100);
  // Hide the entire card when the user has played zero matches — the
  // "0 matches played this season" header on the dashboard already
  // tells the story, no need for an empty grid of zeros.
  const matchesPlayed = (t.wins ?? 0) + (t.losses ?? 0);
  if (matchesPlayed === 0) {
    return null;
  }

  return (
    <div data-testid="combat-stats-card" className="glass-strong rounded-3xl p-5 border-2 border-[#14b8a6]/35 space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-crosshairs mr-1" />COMBAT STATS
          <span
            className="ml-2 text-[9px] text-white/45 tracking-normal normal-case cursor-help"
            title="Server-computed from confirmed match results. Disputes can be filed via the in-app dispute flow."
          >
            <i className="fas fa-circle-info" /> server-computed
          </span>
        </p>
        {(d.per_league || []).length > 0 && (
          <select
            value={activeLeagueId || ""}
            onChange={(e) => { setActiveLeagueId(e.target.value || null); }}
            data-testid="combat-stats-league-select"
            className="bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-[10px] text-white"
          >
            <option value="">ALL LEAGUES</option>
            {(d.per_league || []).map((l) => (
              <option key={l.league_id} value={l.league_id}>
                {l.league_name || `Untitled league · ${(l.league_id || "").slice(-6)}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Big totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="K/D" value={t.kd ?? 0} accent="#14b8a6" testid="stat-kd" />
        <Stat label="WIN RATE" value={`${winRate}%`} accent="#14b8a6" testid="stat-winrate" />
        <Stat label="W·L" value={`${t.wins ?? 0}·${t.losses ?? 0}`} accent="#FFFFFF" testid="stat-wl" />
        <Stat label="ASSISTS" value={t.assists ?? 0} accent="#14b8a6" testid="stat-assists" />
      </div>

      <p className="text-[11px] text-white/55 flex flex-wrap gap-3 -mt-1">
        <span><i className="fas fa-skull-crossbones text-white/45 mr-1" />Kills <b className="text-white">{t.kills ?? 0}</b></span>
        <span><i className="fas fa-skull text-white/45 mr-1" />Deaths <b className="text-white">{t.deaths ?? 0}</b></span>
        <span><i className="fas fa-circle-play text-white/45 mr-1" />Videos <b className="text-white">{t.videos ?? 0}</b></span>
      </p>

      {/* Per-league breakdown */}
      {(d.per_league || []).length > 0 && (
        <div data-testid="combat-stats-per-league">
          <p className="text-[10px] tracking-[3px] text-[#a3a3ac]">BY LEAGUE</p>
          <ul className="mt-2 space-y-1">
            {d.per_league.slice(0, 6).map((row) => (
              <li
                key={row.league_id}
                className="flex items-center gap-3 rounded-xl px-3 py-2 bg-black/30 border border-white/10 text-[12px]"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-white truncate">
                    <span className="text-white/45 text-[10px] tracking-widest mr-1">{(row.game || "").toUpperCase()}</span>
                    {row.league_name || `Untitled league · ${(row.league_id || "").slice(-6)}`}
                  </p>
                </div>
                <span className="text-[10px] text-white/55">{row.wins}W · {row.losses}L</span>
                <span className="text-[10px] text-[#14b8a6] font-bold">{row.kd} KD</span>
                <span className="text-[10px] text-[#14b8a6] font-bold">{Math.round((row.win_rate || 0) * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Head-to-head — only with league filter */}
      {(d.head_to_head || []).length > 0 && (
        <div data-testid="combat-stats-h2h">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6]">
            <i className="fas fa-swords mr-1" />HEAD-TO-HEAD
          </p>
          <ul className="mt-2 space-y-1">
            {(d.head_to_head || []).slice(0, 5).map((row) => {
              const total = row.matches || 0;
              const wr = Math.round((row.win_rate || 0) * 100);
              return (
                <li
                  key={row.team_id}
                  data-testid={`h2h-row-${row.team_id}`}
                  className="flex items-center gap-2 rounded-xl px-3 py-2 bg-black/30 border border-white/10 text-[12px]"
                >
                  <AvatarChip avatarId={row.avatar_id} pfpUrl={row.pfp_url} size={26} />
                  <div className="min-w-0 flex-1">
                    <p className="text-white truncate">{row.name || row.team_id}</p>
                  </div>
                  <span className="text-[10px] text-white/55">{total}m</span>
                  <span className="text-[10px] text-[#14b8a6]">{row.wins}W</span>
                  <span className="text-[10px] text-[#14b8a6]">{row.losses}L</span>
                  <span className={`text-[10px] font-bold ${wr >= 50 ? "text-[#14b8a6]" : "text-[#14b8a6]"}`}>{wr}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="rounded-2xl bg-black/40 border border-white/10 p-3 text-center">
      <p className="text-[9px] tracking-[3px] text-white/45">{label}</p>
      <p
        data-testid={testid}
        className="font-display text-2xl mt-1"
        style={{ color: accent }}
      >
        {value}
      </p>
    </div>
  );
}

export default CombatStatsCard;
